import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getUserInfo, getUserDevices, getUserGroups,
    getUserApplications, getUserDelegations, getUserOrganization,
} from '../../../api/internal/usersInternalApi';
import { Card, CardTitle, Notice, Pill, EmptyState, Spinner, SpinnerContainer } from '../../ui';
import {
    DeviceHero as FicheHero, PostureBadge, HealthStrip, HealthTile,
    DeviceLayout as FicheLayout, DeviceNav as FicheNav, NavItem,
    SpecGrid, SpecFields, Muted, MonoCell, LienIcone,
} from '../../security/security.styled';
import { Champ } from '../../security/champ';
import { UserGroups } from './groups/groups';
import { UserDevices } from './devices/devices';
import { UserApplications } from './applications/applications';
import { UserDelegations } from './delegations/delegations';
import { UserOrganization } from './organization/organization';
import { calculerVerdict, attributsRenseignes, panneauPertinent } from './userVerdict';
import { initiales } from './personne';

/* ═══════════════════════════════════════════════════════════════════
   Fiche utilisateur, sur le squelette de la fiche appareil : bandeau
   d'identité avec verdict, tuiles cliquables qui mènent à leur section,
   navigation groupée par thème, un panneau à la fois.

   Les primitives de mise en page sont importées de security.styled et
   renommées à l'usage : elles sont génériques malgré leur nom d'origine, et
   dupliquer 150 lignes de CSS pour les deux fiches les ferait diverger.
   ═══════════════════════════════════════════════════════════════════ */

const PANNEAUX = [
    { cle: 'apercu', libelle: "Vue d'ensemble", icone: 'bx-layout' },
    {
        groupe: 'Identité',
        items: [
            { cle: 'compte', libelle: 'Compte', icone: 'bx-id-card' },
            { cle: 'organisation', libelle: 'Organisation', icone: 'bx-sitemap' },
            { cle: 'attributs', libelle: 'Attributs', icone: 'bx-badge-check' },
        ],
    },
    {
        groupe: 'Accès',
        items: [
            { cle: 'groupes', libelle: 'Groupes', icone: 'bx-group' },
            { cle: 'applications', libelle: 'Applications', icone: 'bx-grid-alt' },
            { cle: 'delegations', libelle: 'Délégations', icone: 'bx-mail-send' },
            { cle: 'roles', libelle: 'Rôles', icone: 'bx-crown' },
        ],
    },
    {
        groupe: 'Équipement',
        items: [
            { cle: 'appareils', libelle: 'Appareils', icone: 'bx-laptop' },
            { cle: 'licences', libelle: 'Licences', icone: 'bx-package' },
        ],
    },
];

// Les onze valeurs constatées de methodsRegistered, en clair. Les noms Graph
// sont exacts mais illisibles dans une fiche.
const LIBELLES_MFA = {
    microsoftAuthenticatorPush: 'Authenticator (notification)',
    microsoftAuthenticatorPasswordless: 'Authenticator (sans mot de passe)',
    softwareOneTimePasscode: 'Code à usage unique',
    windowsHelloForBusiness: 'Windows Hello',
    mobilePhone: 'Téléphone mobile',
    officePhone: 'Téléphone fixe',
    email: 'Courriel',
    qrCode: 'Code QR',
    passKeySynced: "Clé d'accès synchronisée",
    passKeyDeviceBound: "Clé d'accès liée à l'appareil",
    passKeyDeviceBoundAuthenticator: "Clé d'accès Authenticator",
};

// Une icône par niveau de verdict. « absent » ne parle pas de bouclier : le
// compte n'est pas en danger, il n'est plus là.
const ICONES_VERDICT = {
    sain: 'bx-shield-quarter',
    dormant: 'bx-time-five',
    absent: 'bx-user-x',
    surveiller: 'bx-shield-x',
    critique: 'bx-shield-x',
};

// customSecurityAttributes est un objet à deux niveaux — { AttributesGuests:
// { Demandeur: '...' } } — et non une valeur simple. On l'aplatit en couples
// lisibles plutôt que de jeter du JSON à l'écran.
function aplatirAttributs(source, prefixe = '') {
    if (!source || typeof source !== 'object') return [];
    return Object.entries(source).flatMap(([cle, valeur]) => {
        const chemin = prefixe ? `${prefixe} · ${cle}` : cle;
        if (valeur && typeof valeur === 'object' && !Array.isArray(valeur)) return aplatirAttributs(valeur, chemin);
        if (valeur === null || valeur === undefined || valeur === '') return [];
        return [{ chemin, valeur: Array.isArray(valeur) ? valeur.join(', ') : String(valeur) }];
    });
}

function formaterDate(valeur) {
    if (!valeur) return null;
    const date = new Date(valeur);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('fr-FR');
}

function depuisQuand(valeur) {
    if (!valeur) return null;
    const jours = Math.floor((Date.now() - new Date(valeur).getTime()) / 86400000);
    if (Number.isNaN(jours)) return null;
    if (jours === 0) return "aujourd'hui";
    if (jours === 1) return 'hier';
    if (jours < 60) return `il y a ${jours} jours`;
    const mois = Math.floor(jours / 30);
    return mois < 24 ? `il y a ${mois} mois` : `il y a ${Math.floor(mois / 12)} ans`;
}

// Le tableau d'une liste liée peut arriver soit brut, soit enveloppé dans
// `value` selon l'endpoint : les deux formes coexistent dans les slices.
function liste(source, actif) {
    if (!actif || !source) return [];
    const donnees = source.value ?? source;
    return Array.isArray(donnees) ? donnees : [];
}

export function GetUsers({ usernameInput }) {
    const dispatch = useDispatch();
    const [panneau, setPanneau] = useState('apercu');

    const { currentUser, isLoading } = useSelector((state) => state.user);
    const { currentDevice, isActiveDevice } = useSelector((state) => state.device);
    const { currentGroup, isActiveGroup } = useSelector((state) => state.group);
    const { currentApplication, isActiveApplication } = useSelector((state) => state.application);
    const { currentDelegation, isActiveDelegation } = useSelector((state) => state.delegation);
    const { currentOrganization } = useSelector((state) => state.organization);

    useEffect(() => {
        if (!currentUser && usernameInput) {
            const controleur = new AbortController();
            const signal = controleur.signal;

            // `getUserInfo` attend un setter d'état de menu, héritage de l'ancienne
            // barre de boutons. La navigation est désormais interne à ce
            // composant : on lui passe une fonction inerte plutôt que de modifier
            // une API partagée avec la page Diff.
            getUserInfo(dispatch, () => {}, usernameInput, signal);
            getUserDevices(dispatch, usernameInput, signal);
            getUserGroups(dispatch, usernameInput, signal);
            getUserApplications(dispatch, usernameInput, signal);
            getUserDelegations(dispatch, usernameInput, signal);
            getUserOrganization(dispatch, usernameInput, signal);

            return () => controleur.abort();
        }
        return undefined;
        // eslint-disable-next-line
    }, []);

    if (isLoading || !currentUser) {
        return (
            <Card>
                <SpinnerContainer><Spinner /></SpinnerContainer>
            </Card>
        );
    }

    const appareils = liste(currentDevice, isActiveDevice);
    const groupes = liste(currentGroup, isActiveGroup);
    const applications = liste(currentApplication, isActiveApplication);
    // Les délégations arrivent en OBJET — { delegationOn, delegationOf } — et non
    // en tableau : `liste` renvoyait donc toujours [], le compteur restait à
    // zéro, et comme le panneau ne s'affiche qu'au-delà de zéro, il était
    // invisible sur les 462 comptes du parc qui portent des délégations.
    const delegationsBrutes = isActiveDelegation ? (currentDelegation?.value ?? currentDelegation) : null;
    const delegations = [
        ...(Array.isArray(delegationsBrutes?.delegationOn) ? delegationsBrutes.delegationOn : []),
        ...(Array.isArray(delegationsBrutes?.delegationOf) ? delegationsBrutes.delegationOf : []),
    ];
    const roles = currentUser.roles || [];
    const licences = currentUser.licenses || [];
    const attributs = attributsRenseignes(currentUser);
    const accesGx = Array.isArray(currentUser.gxAccess) ? currentUser.gxAccess : [];
    const methodes = currentUser.authMethods?.methodsRegistered || [];
    const parDefaut = currentUser.authMethods?.defaultMfaMethod;
    const methodeParDefaut = !parDefaut || parDefaut === 'none' ? null : (LIBELLES_MFA[parDefaut] || parDefaut);
    const attributsSecurite = aplatirAttributs(currentUser.customSecurityAttributes);
    const invite = currentUser.userType === 'Guest';
    // Le compte a disparu du dernier balayage Entra. C'est le fait dominant de
    // la fiche : il change le verdict, la mise en forme du bandeau, le ton des
    // tuiles et la lecture de chaque champ collecté.
    const absent = currentUser.isDeleted === true;
    const verdict = calculerVerdict(currentUser);
    const mfa = currentUser.authMethods?.isMfaRegistered === true;

    const compteurs = {
        appareils: appareils.length,
        groupes: groupes.length,
        applications: applications.length,
        delegations: delegations.length,
        roles: roles.length,
        licences: licences.length,
        attributs: attributs.length,
        organisation: currentOrganization ? 1 : (currentUser.manager ? 1 : 0),
    };

    const visible = (cle) => panneauPertinent(cle, { invite, compteurs });

    // Si la section ouverte devient hors sujet — passage d'un membre à un invité
    // par la recherche —, on retombe sur la vue d'ensemble plutôt que d'afficher
    // un panneau vide.
    const panneauActif = visible(panneau) ? panneau : 'apercu';

    const tuiles = [
        {
            cle: 'compte',
            icone: 'bx-key',
            valeur: mfa ? 'MFA actif' : 'Sans MFA',
            libelle: 'Sécurité du compte',
            ton: mfa ? 'ok' : (licences.length > 0 || roles.length > 0 ? 'warn' : 'accent'),
        },
        { cle: 'appareils', icone: 'bx-laptop', valeur: compteurs.appareils, libelle: 'Appareils', ton: 'accent' },
        { cle: 'groupes', icone: 'bx-group', valeur: compteurs.groupes, libelle: 'Groupes', ton: 'accent' },
        { cle: 'applications', icone: 'bx-grid-alt', valeur: compteurs.applications, libelle: 'Applications', ton: 'accent' },
        { cle: 'delegations', icone: 'bx-mail-send', valeur: compteurs.delegations, libelle: 'Délégations', ton: 'accent' },
        { cle: 'roles', icone: 'bx-crown', valeur: compteurs.roles, libelle: 'Rôles privilégiés', ton: roles.length > 0 && !mfa ? 'danger' : 'rssi' },
    ]
        .filter((t) => visible(t.cle))
        // Sur un compte hors annuaire, un « sans MFA » en ambre ou un « rôle
        // privilégié » en rouge appelleraient une action sur un objet qui n'est
        // plus là. Les compteurs restent, l'alarme tombe.
        .map((t) => (absent ? { ...t, ton: 'neutral' } : t));

    const derniereConnexion = currentUser.signInActivity?.lastSignInDateTime;
    const photo = currentUser.profilePicture;

    const carteCompte = (
        <Card>
            <CardTitle>Compte</CardTitle>
            <SpecFields>
                <Champ label="Type de compte">
                    {invite ? 'Invité externe' : 'Membre'}
                    {currentUser.companyName ? ` · ${currentUser.companyName}` : ''}
                </Champ>
                {/* « Activé » sur un compte disparu est le dernier état connu,
                    pas l'état actuel : 451 des 672 comptes hors annuaire portent
                    encore cette valeur. La pastille dit l'absence, le gris dit
                    ce qu'Entra en savait la dernière fois. */}
                <Champ label="État">
                    {absent ? (
                        <>
                            <Pill $tone="warn">Hors annuaire</Pill>
                            {' '}
                            <Muted>
                                dernier état connu :
                                {currentUser.accountEnabled === true
                                    ? ' activé'
                                    : currentUser.accountEnabled === false ? ' désactivé' : ' inconnu'}
                            </Muted>
                        </>
                    ) : (
                        <Pill $tone={currentUser.accountEnabled ? 'ok' : 'danger'}>
                            {currentUser.accountEnabled ? 'Activé' : 'Désactivé'}
                        </Pill>
                    )}
                </Champ>
                <Champ label="UserPrincipalName" className="mono" full>{currentUser.userPrincipalName}</Champ>
                <Champ label="Mail" className="mono">{currentUser.mail}</Champ>
                <Champ label="Trigramme" className="mono">{currentUser.employeeId}</Champ>
                <Champ label="Étoile">{currentUser.department}</Champ>
                <Champ label="Titre du poste">{currentUser.jobTitle}</Champ>
                <Champ label="Manager">{currentUser.manager}</Champ>
                <Champ label="Mobile">{currentUser.mobilePhone}</Champ>
                <Champ label="Ville">{currentUser.city}</Champ>
                <Champ label="Créé le">{formaterDate(currentUser.createdDateTimeAzure)}</Champ>
                <Champ label="ObjectId" className="mono" full>{currentUser.objectId}</Champ>
            </SpecFields>
        </Card>
    );

    const carteSecurite = (
        <Card>
            <CardTitle>Sécurité du compte</CardTitle>
            <SpecFields>
                <Champ label="MFA enregistré">
                    <Pill $tone={mfa ? 'ok' : 'warn'}>{mfa ? 'Oui' : 'Non'}</Pill>
                </Champ>
                <Champ label="MFA possible">
                    <Pill $tone={currentUser.authMethods?.isMfaCapable ? 'ok' : 'warn'}>
                        {currentUser.authMethods?.isMfaCapable ? 'Oui' : 'Non'}
                    </Pill>
                </Champ>
                {/* Le champ réellement collecté est defaultMfaMethod, renseigné
                    sur 3 742 comptes — « none » pour 2 795 d'entre eux. Le nom
                    userPreferredMethodForSecondaryAuthentication, documenté dans
                    le prompt de la requête IA, n'existe sur aucune fiche. */}
                <Champ label="Méthode par défaut">
                    {methodeParDefaut}
                </Champ>
                <Champ label="Méthodes enregistrées" full>
                    {methodes.length > 0
                        ? (
                            <span style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {methodes.map((methode) => (
                                    <Pill key={methode} $tone="accent">{LIBELLES_MFA[methode] || methode}</Pill>
                                ))}
                            </span>
                        )
                        : null}
                </Champ>
                <Champ label="Rôles privilégiés">
                    {roles.length > 0 ? `${roles.length}` : null}
                </Champ>
                <Champ label="Dernière connexion">
                    {derniereConnexion
                        ? <>{formaterDate(derniereConnexion)} <Muted>· {depuisQuand(derniereConnexion)}</Muted></>
                        : null}
                </Champ>
                <Champ label="Mot de passe changé">
                    {currentUser.lastPasswordChangeDateTime
                        ? <>{formaterDate(currentUser.lastPasswordChangeDateTime)} <Muted>· {depuisQuand(currentUser.lastPasswordChangeDateTime)}</Muted></>
                        : null}
                </Champ>
                {/* gxAccess est un TABLEAU de { etoile, actif, profil } sur 921
                    comptes — le rendre brut faisait planter React, qui itérait
                    le tableau et tombait sur des objets. */}
                <Champ label="Accès GX" full>
                    {accesGx.length > 0
                        ? (
                            <span style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {accesGx.map((acces, index) => (
                                    <Pill key={`${acces.etoile}-${index}`} $tone={acces.actif === 'Oui' ? 'ok' : 'neutral'}>
                                        {acces.etoile}
                                        {acces.profil ? ` · ${acces.profil}` : ''}
                                        {acces.actif === 'Oui' ? '' : ' (inactif)'}
                                    </Pill>
                                ))}
                            </span>
                        )
                        : null}
                </Champ>
                <Champ label="Boîte partagée">{currentUser.isBAL === true ? 'Oui' : currentUser.isBAL === false ? 'Non' : null}</Champ>
            </SpecFields>
        </Card>
    );

    const contenu = {
        apercu: (
            <>
                {!absent && verdict.motifs.length > 0 && verdict.niveau !== 'sain' && (
                    <Notice $tone={verdict.ton} style={{ marginBottom: '12px' }}>
                        <i className={`bx ${verdict.niveau === 'critique' ? 'bx-shield-x' : 'bx-error-circle'}`} />
                        <span>
                            {verdict.motifs.join(' · ')}.
                            {verdict.niveau === 'critique' && ' 14 comptes du parc portent un rôle privilégié sans authentification forte.'}
                            {verdict.niveau === 'surveiller' && !mfa && licences.length > 0
                                && " 107 comptes licenciés actifs sont sans MFA — à distinguer des 2 794 comptes sans MFA du parc, dont 2 396 invités sans licence ni appareil."}
                            {verdict.niveau === 'dormant' && ' 782 invités ne se sont jamais connectés.'}
                        </span>
                    </Notice>
                )}
                <SpecGrid>
                    {carteCompte}
                    {carteSecurite}
                </SpecGrid>
            </>
        ),
        compte: <SpecGrid>{carteCompte}{carteSecurite}</SpecGrid>,
        organisation: <Card><UserOrganization /></Card>,
        attributs: (
            <Card>
                <CardTitle>Attributs</CardTitle>
                {attributs.length === 0 && attributsSecurite.length === 0 ? (
                    <EmptyState>
                        <i className="bx bx-badge-check" />
                        <h3>Aucun attribut renseigné</h3>
                        <p>Sur les quinze attributs d'extension déclarés, sept seulement sont utilisés dans le parc.</p>
                    </EmptyState>
                ) : (
                    <SpecFields>
                        {attributs.map((a) => (
                            <Champ key={a.numero} label={`Attribut d'extension ${a.numero}`}>{a.valeur}</Champ>
                        ))}
                        {attributsSecurite.map((attribut) => (
                            <Champ key={attribut.chemin} label={attribut.chemin} full>
                                {attribut.valeur}
                            </Champ>
                        ))}
                    </SpecFields>
                )}
            </Card>
        ),
        groupes: <Card><UserGroups /></Card>,
        applications: <Card><UserApplications /></Card>,
        delegations: <Card><UserDelegations /></Card>,
        roles: (
            <Card>
                <CardTitle>
                    Rôles privilégiés
                    {!mfa && <Pill $tone="danger" style={{ marginLeft: '8px' }}>sans MFA</Pill>}
                </CardTitle>
                {roles.length === 0 ? (
                    <EmptyState>
                        <i className="bx bx-crown" />
                        <h3>Aucun rôle privilégié</h3>
                        <p>78 comptes sur 4 339 portent au moins un rôle Entra.</p>
                    </EmptyState>
                ) : (
                    <SpecFields>
                        {roles.map((role, index) => (
                            <Champ key={`${role.displayName}-${index}`} label={role.displayName} full>
                                <Muted>{role.membership || 'attribution directe'}</Muted>
                            </Champ>
                        ))}
                    </SpecFields>
                )}
            </Card>
        ),
        appareils: <Card><UserDevices /></Card>,
        licences: (
            <Card>
                <CardTitle>Licences</CardTitle>
                {licences.length === 0 ? (
                    <EmptyState>
                        <i className="bx bx-package" />
                        <h3>Aucune licence</h3>
                        <p>{invite ? "Un seul invité du parc porte une licence." : 'Ce compte ne consomme aucune licence.'}</p>
                    </EmptyState>
                ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {licences.map((licence) => <Pill key={licence} $tone="accent">{licence}</Pill>)}
                    </div>
                )}
            </Card>
        ),
    };

    return (
        <>
            <FicheHero $absent={absent}>
                {photo ? (
                    <img
                        src={`data:image/jpeg;base64,${photo}`}
                        alt={`Photo de ${currentUser.displayName}`}
                        style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                    />
                ) : invite ? (
                    <i className="bx bx-user-plus" />
                ) : (
                    // Les initiales plutôt que l'image par défaut quand le compte a
                    // un nom : 833 comptes sur 4 339 ont une photo, la collecte n'en
                    // demande qu'aux membres salariés et contractuels.
                    <i style={{ fontStyle: 'normal', fontWeight: 800, fontSize: '1rem' }}>
                        {initiales(currentUser.displayName)}
                    </i>
                )}

                <div>
                    <div className="eyebrow">
                        Utilisateur · {invite ? 'Invité externe' : 'Membre'}
                        {absent && ' · hors annuaire'}
                    </div>
                    <h2 className="name">{currentUser.displayName}</h2>
                    <div className="facts">
                        {currentUser.employeeId && <span><b>{currentUser.employeeId}</b></span>}
                        {currentUser.jobTitle && <span>{currentUser.jobTitle}</span>}
                        {currentUser.department && <span>{currentUser.department}</span>}
                        <span><MonoCell>{currentUser.userPrincipalName}</MonoCell></span>
                        {currentUser.manager && <span>Manager <b>{currentUser.manager}</b></span>}
                        {invite && currentUser.sponsors?.length > 0 && (
                            <span>Invité par <b>{currentUser.sponsors[0].userPrincipalName}</b></span>
                        )}
                        <span>
                            {derniereConnexion
                                ? <>Dernière connexion <b>{depuisQuand(derniereConnexion)}</b></>
                                : <b>Jamais connecté</b>}
                        </span>
                        {/* Identifiant LinkedIn, porté par l'attribut d'extension 1
                            sur 164 comptes. Le lien n'apparaît que s'il existe :
                            l'ancienne page renvoyait vers l'accueil de LinkedIn
                            quand l'attribut était vide. */}
                        {currentUser.onPremisesExtensionAttributes?.extensionAttribute1 && (
                            <LienIcone
                                href={`https://www.linkedin.com/in/${currentUser.onPremisesExtensionAttributes.extensionAttribute1}`}
                                target="_blank"
                                rel="noreferrer"
                                title={`Profil LinkedIn de ${currentUser.displayName}`}
                                aria-label="Profil LinkedIn"
                            >
                                <i className="bx bxl-linkedin" />
                            </LienIcone>
                        )}
                    </div>
                </div>

                <PostureBadge $tone={verdict.ton}>
                    <div className="verdict">
                        <i className={`bx ${ICONES_VERDICT[verdict.niveau] || 'bx-shield-x'}`} />
                        {verdict.mot}
                    </div>
                    <div className="reasons">
                        {verdict.motifs.length > 0 ? verdict.motifs.join(' · ') : 'aucun signal'}
                    </div>
                </PostureBadge>
            </FicheHero>

            {/* Le fait le plus important de la fiche, dit en mots et avant tout
                le reste : ce qui suit ne décrit plus un compte existant. Placé
                sous l'identité et non dans un panneau, il reste à l'écran quelle
                que soit la section consultée. */}
            {absent && (
                <Notice $tone="warn" style={{ marginBottom: '12px' }}>
                    <i className="bx bx-user-x" />
                    <span>
                        <strong>Ce compte n'est plus présent dans l'annuaire Entra ID.</strong>
                        {' '}
                        La dernière collecte ne l'a pas retrouvé. Tout ce qui est affiché ici est un
                        instantané figé au dernier passage où Entra le connaissait : état du compte,
                        licences, groupes, applications, appareils et méthodes d'authentification ne
                        sont plus rafraîchis.
                        {currentUser.accountEnabled === true
                            && " La mention « Activé » est donc le dernier état connu, pas l'état actuel."}
                        {licences.length > 0
                            && ` ${licences.length} licence${licences.length > 1 ? 's' : ''} `
                                + `${licences.length > 1 ? 'restent' : 'reste'} inscrite${licences.length > 1 ? 's' : ''} `
                                + "sur la fiche : la suppression du compte ne les libère pas toujours, "
                                + "à vérifier dans le centre d'administration."}
                    </span>
                </Notice>
            )}

            <HealthStrip>
                {tuiles.map((tuile) => (
                    <HealthTile
                        key={tuile.cle}
                        type="button"
                        $tone={tuile.ton}
                        $active={panneauActif === tuile.cle}
                        onClick={() => setPanneau(tuile.cle)}
                        title={`Voir la section ${tuile.libelle}`}
                    >
                        <i className={`bx ${tuile.icone}`} />
                        <span>
                            <span className="value">{tuile.valeur}</span>
                            <br />
                            <span className="label">{tuile.libelle}</span>
                        </span>
                    </HealthTile>
                ))}
            </HealthStrip>

            <FicheLayout>
                <FicheNav>
                    {PANNEAUX.map((section) => (section.groupe ? (
                        section.items.some((item) => visible(item.cle)) && (
                            <div key={section.groupe}>
                                <div className="group">{section.groupe}</div>
                                {section.items.filter((item) => visible(item.cle)).map((item) => (
                                    <NavItem
                                        key={item.cle}
                                        type="button"
                                        $active={panneauActif === item.cle}
                                        onClick={() => setPanneau(item.cle)}
                                    >
                                        <i className={`bx ${item.icone}`} />
                                        {item.libelle}
                                        {compteurs[item.cle] > 0 && <span className="count">{compteurs[item.cle]}</span>}
                                    </NavItem>
                                ))}
                            </div>
                        )
                    ) : (
                        <NavItem
                            key={section.cle}
                            type="button"
                            $active={panneauActif === section.cle}
                            onClick={() => setPanneau(section.cle)}
                        >
                            <i className={`bx ${section.icone}`} />
                            {section.libelle}
                        </NavItem>
                    )))}
                </FicheNav>

                <div>{contenu[panneauActif]}</div>
            </FicheLayout>
        </>
    );
}
